import { useRef, useState } from 'react';

import { useRouter } from 'next/router';
import { Row, useSortBy, useTable } from 'react-table';

import { useAppSelector } from '@/hooks/redux';
import { selectRequestedLoan } from '@/redux/loans/loansSlice';
import { cn } from '@/utils/helper';

interface ITableUI {
  columns: any[];
  data: any[];
  handleNavigateDetails?: (id: any) => void;
  loading?: boolean;
  crypto?: boolean;
  hoverClass?: string;
  checkedTr?: (id: any) => boolean;
  borderHover?: string;
  setRowProps?: (row: Row<any>) => any;
  enableDefaultSort?: boolean;
  wrapperStyle?: string;
  tdStyle?: string;
  thStyle?: string;
  trStyle?: string;
  sortKey?: string;
  dirKey?: string;
  sortIconClassName?: string;
  thTextClassName?: string;
}
const TableUi: React.FC<ITableUI> = ({ checkedTr, columns, data, handleNavigateDetails, crypto, sortIconClassName, loading, wrapperStyle, tdStyle, trStyle, thStyle, hoverClass, borderHover, thTextClassName, setRowProps = () => ({}), enableDefaultSort, sortKey = 'sort', dirKey = 'dir' }) => {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data,
    },
    useSortBy
  );
  const router = useRouter();
  const requestedLoan = useAppSelector(selectRequestedLoan);
  const handleSort = (id: string) => {
    let direction = 'ASC';

    if (router.query[sortKey] === id && router.query[dirKey] === 'ASC') {
      direction = 'DESC';
    }

    router.push(
      {
        query: { ...router.query, [sortKey]: id, [dirKey]: direction },
        pathname: router.pathname,
      },
      undefined,
      { scroll: false }
    );
  };

  const scrollRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => setIsDragging(false);

  if (data.length) {
    return (
      <div ref={scrollRef} className={cn('overflow-x-auto scrollbar-hide w-full', wrapperStyle)} onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}>
        <table {...getTableProps()} className={cn('w-full border-separate border-spacing-y-0 select-none')}>
          <thead>
            {headerGroups.map((headerGroup) => {
              const { key, ...groupProps } = headerGroup.getHeaderGroupProps();
              return (
                <tr key={key} {...groupProps}>
                  {headerGroup.headers.map((column) => {
                    // @ts-ignore
                    const { key: headerKey, ...headProps } = column.getHeaderProps(column.getSortByToggleProps());
                    return (
                      // @ts-ignore
                      <th key={headerKey} className={cn('first:pl-6', thStyle, column.thClassName)} {...headProps}>
                        {
                          // @ts-ignore
                          <p className={cn('text-[10px]/3 lg:text-sm max-w-max', column.thPClassName)}>
                            {column.render('Header', {
                              sortIconClassName,
                              ...(enableDefaultSort && {
                                sortHandler: () => {
                                  // @ts-ignore
                                  handleSort(column.sortId || column.id);
                                },
                                id: column.id,
                                // @ts-ignore
                                sortId: column.sortId,
                                // @ts-ignore
                                sortKey,
                                // @ts-ignore
                                dirKey,
                                // @ts-ignore
                                textClassName: thTextClassName,
                              }),
                            })}
                          </p>
                        }
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row: any) => {
              prepareRow(row);
              const { key, className, ...rowProps } = row.getRowProps(setRowProps(row));
              function getActiveRow() {
                if (row.original.id === requestedLoan?.id) return 'dark:!bg-coal !bg-light-purple';
                return 'dark:!bg-jet-black';
              }
              return (
                <tr
                  key={key}
                  {...rowProps}
                  className={cn('group !bg-white dark:!bg-jet-black', crypto ? 'cursor-pointer' : '', loading && 'animate-pulse', checkedTr && checkedTr(row.original.collection_id) ? '!bg-light-purple ' : '', hoverClass, className, requestedLoan && getActiveRow(), trStyle ?? '')}
                  onClick={() => {
                    if (handleNavigateDetails) {
                      if (crypto) {
                        handleNavigateDetails(row.original.slug);
                      } else {
                        handleNavigateDetails(row.original.collection_id);
                      }
                    }
                  }}
                >
                  {row.cells.map((cell: any) => {
                    const { key: cellKey, ...celProps } = cell.getCellProps();
                    return (
                      <td key={cellKey} className={cn(crypto ? 'w-max py-3' : 'text-coal-250 text-sm ', tdStyle ?? '', 'border-b border-b-shade-blue-grey dark:border-b-coal-border last:min-w-fit', borderHover, checkedTr && checkedTr(row.original.collectionid) ? '!py-[10px] [&*]:!text-primary [&_path]:fill-primary' : '', cell.column.className)} {...celProps}>
                        {cell.render('Cell')}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }
  return null;
};

export default TableUi;
